import type { BreadcrumbItem } from "~/utils/seo";

type BreadcrumbsProps = {
  /**
   * The trail, root first. The last entry is the current page and is rendered
   * as plain text — a self-link is noise for both users and crawlers.
   */
  items: BreadcrumbItem[];
  className?: string;
};

/**
 * Visible breadcrumb trail (Home › Blog › Post).
 *
 * Pairs with `breadcrumbJsonLd` in `~/utils/seo` — emit both so the markup a
 * user sees and the structured data a crawler reads describe the same trail.
 * Rendering one without the other is what Google flags as a mismatch.
 *
 * The separators are `aria-hidden` decoration; the `<nav aria-label>` plus the
 * ordered list carry the real semantics, and the current page is marked with
 * `aria-current="page"`.
 *
 * Crumbs are plain `<a href>` rather than a router `Link` because the trail is
 * built from arbitrary path strings, which TanStack's typed `to` prop rejects
 * — the same reason `Button` takes an `href`.
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const trail = items.filter((i) => i.name);
  if (trail.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-body text-base text-charcoal">
        {trail.map((item, index) => {
          const isLast = index === trail.length - 1;
          return (
            <li key={`${item.name}-${index}`} className="flex items-center gap-x-2">
              {index > 0 && (
                <span aria-hidden="true" className="text-gray-500">
                  /
                </span>
              )}
              {item.url && !isLast ? (
                <a
                  href={item.url}
                  className="underline decoration-gray-40 underline-offset-2 transition hover:text-teal-dark hover:decoration-teal-dark"
                >
                  {item.name}
                </a>
              ) : (
                <span
                  className={isLast ? "text-gray-500" : undefined}
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.name}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
